import { HttpClient } from '@angular/common/http';
import { AppRequestMethod, extractDataFromApiResponse } from '@core/shared/request';
import { getAppRequestMeta } from '@core/shared/app-request';
import { ClassTransformService, Model, ModelFactoryProvider } from '@core/shared/model';
import { saveAs } from 'file-saver';
import { map, share } from 'rxjs/operators';
import { ClientCacheService, getClientCacheModelId, getHeadersForEnabledClientCache, getUtilityHeadersForClientCache } from '../../client-cache';
import * as i0 from "@angular/core";
import * as i1 from "@angular/common/http";
import * as i2 from "@core/shared/model";
import * as i3 from "../../client-cache";
// TODO: Move to ./api
export let AppRequestService = /*#__PURE__*/(() => {
  class AppRequestService {
    constructor(http, classTransform, modelFactoryProvider, clientCache) {
      this.http = http;
      this.classTransform = classTransform;
      this.modelFactoryProvider = modelFactoryProvider;
      this.clientCache = clientCache;
    }
    sendRequest(reqModel, data, options = {}) {
      if (!(reqModel.prototype instanceof Model)) throw new Error('AppRequest models must inherit Model!');
      const requestClass = reqModel;
      const model = this.modelFactoryProvider.createModelFactory(requestClass).fromData(data || {});
      const meta = getAppRequestMeta(requestClass);
      if (meta.config.multipart) throw new Error('multipart-AppRequests must be sent using sendMultiPartRequest()!');
      let request;
      const path = this.generateApiPath(model, meta);
      const reqData = this.classTransform.instanceToPlain(model);
      const headers = this.createHeaders(reqModel, meta, options);
      if (meta.config.httpMethod === AppRequestMethod.POST) {
        request = this.http.post(path, reqData, {
          headers
        });
      } else if (meta.config.httpMethod === AppRequestMethod.GET) {
        request = this.http.get(path, {
          params: this._createGetRequestParams(meta, reqData, model),
          headers
        });
      } else {
        throw new Error('unsupported httpMethod' + meta.config.httpMethod);
      }
      const sharedReq = request.pipe(share());
      return this.transformResponse(requestClass, sharedReq);
    }
    // NOTE: in case of MultiPart formData, we cannot use class-transformer. So the incoming data will be used without any further processing!
    sendMultiPartRequest(reqModel, data, options = {}) {
      if (!(reqModel.prototype instanceof Model)) throw new Error('AppRequest models must inherit Model!');
      const requestClass = reqModel;
      const meta = getAppRequestMeta(requestClass);
      if (!meta.config.multipart) throw new Error('regular AppRequests cannot be sent using sendMultiPartRequest()!');
      let request;
      const path = this.generateApiPath({}, meta);
      const headers = this.createHeaders(reqModel, meta, options);
      let formData;
      if (data instanceof FormData) {
        formData = data; // for some reason, jest will error if not explicitly stating "data as Formdata"
      } else {
        formData = new FormData();
        Object.entries(data).forEach(([key, value]) => {
          formData.append(key, value);
        });
      }
      if (meta.config.httpMethod === AppRequestMethod.POST) {
        console.log('send request using formData ', formData);
        request = this.http.post(path, formData, {
          headers
        });
      } else {
        throw new Error('unsupported httpMethod' + meta.config.httpMethod);
      }
      const sharedReq = request.pipe(share());
      return this.transformResponse(requestClass, sharedReq);
    }
    transformResponse(requestClass, pendingRequest) {
      const ResponseModel = requestClass['Response'];
      if (ResponseModel && ResponseModel.prototype instanceof Model) {
        return pendingRequest.pipe(map(response => {
          const parsedDataModel = this.classTransform.plainToInstance(ResponseModel, extractDataFromApiResponse(response, requestClass.name));
          return parsedDataModel;
        }));
      } else {
        return pendingRequest.pipe(map(response => {
          return extractDataFromApiResponse(response, requestClass.name);
        }));
      }
    }
    downloadFileRequest(model, param2, param3 = undefined) {
      let defaultFileName;
      if (!(model instanceof Model)) {
        model = this.modelFactoryProvider.createModelFactory(model).fromData(param2);
        defaultFileName = param3;
      } else {
        defaultFileName = param2;
      }
      let request;
      const meta = getAppRequestMeta(model.constructor);
      const path = this.generateApiPath(model, meta);
      const reqData = this.classTransform.instanceToPlain(model);
      // any fixes tslint thinking the object would not match parameters
      const config = {
        responseType: 'blob',
        observe: 'response'
      };
      if (meta.config.httpMethod !== AppRequestMethod.GET && meta.config.httpMethod !== AppRequestMethod.POST) {
        throw new Error('download Requests can only use GET or POST');
      }
      if (meta.config.httpMethod === AppRequestMethod.POST) {
        request = this.http.post(path, reqData, config);
      } else {
        // Method GET
        request = this.http.get(path, {
          params: this._createGetRequestParams(meta, reqData, model),
          ...config
        });
      }
      request = request.pipe(map(response => {
        const dispHeader = response.headers.get('content-disposition');
        const extractFileName = /filename="([^"]*)"/gm;
        const match = extractFileName.exec(dispHeader);
        const filename = match && match.length ? match[1] : defaultFileName;
        saveAs(response.body, filename);
        return null;
      }), share());
      request.subscribe();
      return request;
    }
    createHeaders(reqModel, meta, options) {
      let headers = {
        ...getUtilityHeadersForClientCache(options.cache)
      };
      if (meta.config.clientCache) {
        const tags = ['feature:' + meta.namespace, 'path:' + meta.namespace + '/' + meta.path, getClientCacheModelId(reqModel)];
        headers = {
          ...headers,
          ...getHeadersForEnabledClientCache(meta.config.clientCache, tags, options.cache)
        };
      }
      if (!IS_PRODUCTION) {
        headers['X-AppRequest-Name'] = reqModel.name;
      }
      return headers;
    }
    // reqData is model converted to plain object
    // angular will apply encoding to these params, but it allows some chars not to be encoded.
    // see comments in ./url-encoded-http-params.ts for details.
    _createGetRequestParams(meta, reqData, model) {
      return reqData;
    }
    generateApiPath(model, meta) {
      // regex to Match ':[name]' in paths given like /something/items/:id/xx
      const matchPathParameters = /\/(:[a-zA-Z]+)(?=\/|$)/gm;
      const path = meta.namespace + '/' + meta.path;
      return path.replace(matchPathParameters, (match, p1, p2) => {
        p1 = p1.substr(1); // strip ':' prefix
        let value;
        if (typeof model[p1] !== 'undefined') {
          value = model[p1];
        } else {
          value = 'UNDEFINED';
        }
        if (typeof value === 'number') return '/' + value.toString();else return '/' + value;
      });
    }
    static {
      this.ɵfac = function AppRequestService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AppRequestService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(i2.ClassTransformService), i0.ɵɵinject(i2.ModelFactoryProvider), i0.ɵɵinject(i3.ClientCacheService));
      };
    }
    static {
      this.ɵprov = /*@__PURE__*/i0.ɵɵdefineInjectable({
        token: AppRequestService,
        factory: AppRequestService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AppRequestService;
})();